import React, { useState, useEffect, useCallback } from 'react';
import { aeAPI, patientAPI, ipdAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { printAEDischargeSummary, printLAMAConsent, printDAMAConsent, printAEDeathCertificate, printHandoverNote } from '../../utils/printSlip';

const TRIAGE_COLORS = { RED: '#ef4444', ORANGE: '#f97316', YELLOW: '#eab308', GREEN: '#22c55e', BLUE: '#3b82f6' };
const TRIAGE_LIMITS = { 1: 0, 2: 10, 3: 30, 4: 60, 5: 120 };

function getElapsed(arrivalTime) {
    return Math.floor((Date.now() - new Date(arrivalTime).getTime()) / 60000);
}
function formatTimer(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const TRIAGE_LEVELS = [
    { level: 1, label: 'T1 — Immediate', color: '#ef4444', example: 'Cardiac arrest, Major trauma, Unconscious' },
    { level: 2, label: 'T2 — Very Urgent', color: '#f97316', example: 'Chest pain, Head injury, RTA' },
    { level: 3, label: 'T3 — Urgent', color: '#eab308', example: 'Abdominal pain, Fever+fits, Fracture' },
    { level: 4, label: 'T4 — Standard', color: '#22c55e', example: 'Minor laceration, Mild pain, Sprain' },
    { level: 5, label: 'T5 — Non-Urgent', color: '#3b82f6', example: 'Prescription, Mild cough/fever' },
];

const CHIEF_COMPLAINTS = [
    'Chest Pain', 'Shortness of Breath', 'Abdominal Pain', 'Fever', 'Head Injury',
    'Road Traffic Accident (RTA)', 'Assault / Trauma', 'Unconscious / Collapse',
    'Seizures / Fits', 'Stroke / Paralysis', 'Poisoning / Overdose', 'Burns',
    'Fracture / Dislocation', 'Allergic Reaction', 'Bleeding', 'Vomiting / Diarrhea',
    'Urinary Problems', 'Eye Injury', 'Back Pain', 'Other'
];

const MCI_TAG_COLORS = {
    'T1-Immediate': { bg: '#dc2626', text: 'white', label: 'T1' },
    'T2-Delayed':   { bg: '#f97316', text: 'white', label: 'T2' },
    'T3-Minor':     { bg: '#22c55e', text: 'white', label: 'T3' },
    'T4-Expectant': { bg: '#1e293b', text: 'white', label: 'T4' },
};

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

const RELATIONS = ['S/O', 'D/O', 'W/O', 'B/O', 'F/O', 'M/O'];
const CITIES = {
    Pakistan: ['Attock','Bahawalpur','Badin','Bannu','Chakwal','Chaman','Charsadda','Dera Ghazi Khan','Dera Ismail Khan','Faisalabad','Ghotki','Gilgit','Gujranwala','Gujrat','Gwadar','Hafizabad','Haripur','Hub','Hunza','Hyderabad','Islamabad','Jacobabad','Jhelum','Jhang','Kalat','Karachi','Kasur','Khairpur','Khanewal','Kohat','Lahore','Larkana','Mansehra','Mardan','Mianwali','Mingora','Mirpur (AJK)','Mirpur Khas','Multan','Muzaffarabad','Muzaffargarh','Narowal','Nawabshah','Nowshera','Okara','Peshawar','Pishin','Quetta','Rahim Yar Khan','Rawalpindi','Rawalakot','Sahiwal','Sanghar','Sargodha','Sheikhupura','Shikarpur','Sialkot','Skardu','Sukkur','Swabi','Thatta','Turbat','Vehari','Wazirabad','Zhob'].sort(),
    Iran: ['Tehran','Mashhad','Isfahan','Karaj','Tabriz','Shiraz','Ahvaz','Qom','Kermanshah','Zahedan','Rasht','Kerman'],
    Afghanistan: ['Kabul','Kandahar','Herat','Mazar-i-Sharif','Jalalabad','Kunduz','Lashkar Gah','Khost','Ghazni'],
    UAE: ['Dubai','Abu Dhabi','Sharjah','Ajman','Ras Al Khaimah','Fujairah','Al Ain'],
    'Saudi Arabia': ['Riyadh','Jeddah','Mecca','Medina','Dammam','Khobar','Tabuk','Abha','Taif','Jubail','Yanbu'],
    Qatar: ['Doha','Al Wakrah','Al Khor','Al Rayyan'],
    Kuwait: ['Kuwait City','Hawalli','Salmiya','Farwaniya','Ahmadi','Jahra'],
    Other: [],
};
const COUNTRIES = Object.keys(CITIES);
const BLINK_STYLE = `@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }`;

function TriageTimer({ arrivalTime, triageLevel }) {
    const [elapsed, setElapsed] = useState(getElapsed(arrivalTime));
    useEffect(() => {
        const t = setInterval(() => setElapsed(getElapsed(arrivalTime)), 30000);
        return () => clearInterval(t);
    }, [arrivalTime]);

    const limit = TRIAGE_LIMITS[triageLevel];
    const overdue = elapsed > limit;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ background: overdue ? '#fee2e2' : '#f0fdf4', color: overdue ? '#dc2626' : '#16a34a', padding: '3px 8px', borderRadius: 5, fontSize: 12, fontWeight: 700, border: `1px solid ${overdue ? '#fca5a5' : '#86efac'}` }}>
                ⏱ {formatTimer(elapsed)}
            </span>
            {overdue && (
                <span style={{ background: '#dc2626', color: 'white', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, animation: 'blink 1s ease-in-out infinite' }}>
                    ⚠ OVERDUE
                </span>
            )}
            {!overdue && limit > 0 && (
                <span style={{ fontSize: 10, color: '#64748b' }}>{limit - elapsed}m left</span>
            )}
        </div>
    );
}

// Disposition Modal
function DispositionModal({ patient, visit, wards, onClose, onDone }) {
    const [disposition, setDisposition] = useState('');
    const [form, setForm] = useState({ diagnosis: '', treatment: '', condition: '', followup: '', admit_ward: '', transfer_hospital: '', destination: '', receiving_team: '', pending: '', allergies: '', notes: '', death_time: '', cause_immediate: '', manner: '' });
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSave = async () => {
        if (!disposition) return toast.error('Select disposition');
        setSaving(true);
        try {
            await aeAPI.setDisposition({
                visit_id: visit.id,
                disposition,
                admit_ward: form.admit_ward || null,
                transfer_hospital: form.transfer_hospital || null,
                death_time: form.death_time || null,
            });
            if (disposition === 'Admitted' && form.admit_ward) {
                try {
                    await ipdAPI.admitPatient({ patient_id: patient.patient_id || visit.patient_id, ward_id: form.admit_ward, admission_source: 'A&E', diagnosis: form.diagnosis });
                    toast.success('Patient admitted to ward via IPD module');
                } catch { toast.warning('Disposition set. Complete IPD admission manually in IPD module.'); }
            }
            toast.success(`Disposition set: ${disposition}`);
            setDone(true);
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to set disposition');
        } finally { setSaving(false); }
    };

    const printDoc = () => {
        const p = { full_name: visit.full_name, mrn: visit.mrn, age_years: visit.age_years, gender: visit.gender, phone: visit.phone, cnic: visit.cnic };
        if (disposition === 'Discharged') printAEDischargeSummary(p, visit, form);
        else if (disposition === 'LAMA') printLAMAConsent(p, visit);
        else if (disposition === 'DAMA') printDAMAConsent(p, visit);
        else if (disposition === 'Death') printAEDeathCertificate(p, visit, form);
        else if (disposition === 'Transfer ICU/OT' || disposition === 'Transfer Hospital') printHandoverNote(p, visit, form);
        else if (disposition === 'Admitted') printHandoverNote(p, visit, { ...form, destination: `Ward: ${form.admit_ward}` });
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div style={{ background: 'white', borderRadius: 14, width: 560, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                    <div>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>Set Disposition</h3>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>{visit.full_name} — {visit.ae_number}</p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 6 }}>Disposition *</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {['Discharged', 'Admitted', 'Transfer ICU/OT', 'Transfer Hospital', 'LAMA', 'DAMA', 'Death'].map(d => (
                            <button key={d} onClick={() => setDisposition(d)}
                                style={{ padding: '10px 14px', borderRadius: 8, border: `2px solid ${disposition === d ? '#0369a1' : '#e2e8f0'}`, background: disposition === d ? '#eff6ff' : 'white', color: disposition === d ? '#0369a1' : '#374151', fontWeight: disposition === d ? 700 : 400, cursor: 'pointer', fontSize: 13, textAlign: 'left' }}>
                                {d === 'Discharged' ? '✅' : d === 'Admitted' ? '🛏' : d === 'Transfer ICU/OT' ? '🚑' : d === 'Transfer Hospital' ? '🏥' : d === 'LAMA' ? '🚶' : d === 'DAMA' ? '🚶' : '✝'} {d}
                            </button>
                        ))}
                    </div>
                </div>

                {disposition && (
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginBottom: 16 }}>
                        {(disposition === 'Discharged' || disposition === 'Admitted') && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Diagnosis</label>
                                    <input style={inputStyle} value={form.diagnosis} onChange={e => sf('diagnosis', e.target.value)} placeholder="Working / Final diagnosis" />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Treatment Given</label>
                                    <input style={inputStyle} value={form.treatment} onChange={e => sf('treatment', e.target.value)} placeholder="Medications, procedures" />
                                </div>
                                {disposition === 'Discharged' && <>
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Discharge Condition</label>
                                        <select style={{ ...inputStyle, background: 'white' }} value={form.condition} onChange={e => sf('condition', e.target.value)}>
                                            <option value="">Select</option>
                                            <option>Stable</option><option>Improved</option><option>Satisfactory</option><option>Critical but Stable</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Follow-up Instructions</label>
                                        <input style={inputStyle} value={form.followup} onChange={e => sf('followup', e.target.value)} placeholder="e.g. Review in 3 days" />
                                    </div>
                                </>}
                                {disposition === 'Admitted' && (
                                    <div style={{ gridColumn: 'span 2' }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Admit to Ward</label>
                                        <select style={{ ...inputStyle, background: 'white' }} value={form.admit_ward} onChange={e => sf('admit_ward', e.target.value)}>
                                            <option value="">Select ward</option>
                                            {wards.map(w => <option key={w.id} value={w.id}>{w.name} ({w.type})</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}

                        {(disposition === 'Transfer ICU/OT' || disposition === 'Transfer Hospital') && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{disposition === 'Transfer ICU/OT' ? 'Destination (ICU / OT / HDU)' : 'Receiving Hospital'}</label>
                                    <input style={inputStyle} value={form.destination || form.transfer_hospital} onChange={e => { sf('destination', e.target.value); sf('transfer_hospital', e.target.value); }} placeholder={disposition === 'Transfer ICU/OT' ? 'e.g. ICU Bed 3' : 'Hospital name'} />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Diagnosis / Reason</label>
                                    <input style={inputStyle} value={form.diagnosis} onChange={e => sf('diagnosis', e.target.value)} placeholder="Working diagnosis" />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Treatment Given</label>
                                    <input style={inputStyle} value={form.treatment} onChange={e => sf('treatment', e.target.value)} placeholder="Medications, procedures done" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Receiving Team</label>
                                    <input style={inputStyle} value={form.receiving_team} onChange={e => sf('receiving_team', e.target.value)} placeholder="Dr. / Team name" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Pending Investigations</label>
                                    <input style={inputStyle} value={form.pending} onChange={e => sf('pending', e.target.value)} placeholder="e.g. CT Head result" />
                                </div>
                            </div>
                        )}

                        {disposition === 'Death' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Time of Death *</label>
                                    <input type="datetime-local" style={inputStyle} value={form.death_time} onChange={e => sf('death_time', e.target.value)} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Manner of Death</label>
                                    <select style={{ ...inputStyle, background: 'white' }} value={form.manner} onChange={e => sf('manner', e.target.value)}>
                                        <option value="">Select</option>
                                        <option>Natural</option><option>Accident</option><option>Homicide</option><option>Suicide</option><option>Undetermined</option>
                                    </select>
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Immediate Cause of Death</label>
                                    <input style={inputStyle} value={form.cause_immediate} onChange={e => sf('cause_immediate', e.target.value)} placeholder="e.g. Cardiac arrest" />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    {!done ? (
                        <button onClick={handleSave} disabled={saving || !disposition}
                            style={{ flex: 1, padding: '11px', background: saving || !disposition ? '#9ca3af' : '#0369a1', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                            {saving ? 'Saving...' : 'Confirm Disposition'}
                        </button>
                    ) : (
                        <button onClick={printDoc}
                            style={{ flex: 1, padding: '11px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                            🖨 Print {disposition === 'Discharged' ? 'Discharge Summary' : disposition === 'LAMA' ? 'LAMA Consent' : disposition === 'DAMA' ? 'DAMA Consent' : disposition === 'Death' ? 'Death Certificate' : 'Handover Note'}
                        </button>
                    )}
                    <button onClick={onClose} style={{ padding: '11px 18px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Close</button>
                </div>
            </div>
        </div>
    );
}

export default function AEDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState({ patients: [], summary: {} });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('ALL');
    const [wards, setWards] = useState([]);

    const [patientSearch, setPatientSearch] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [patientDetails, setPatientDetails] = useState({ full_name: '', phone: '', cnic: '', gender: '', age_years: '', name_relation: 'S/O', father_name: '', country: 'Pakistan', city: '' });
    const [visitForm, setVisitForm] = useState({ arrival_mode: 'Walk-in', triage_level: '', chief_complaint: '', is_mlc: false, mlc_type: '' });
    const [submitLoading, setSubmitLoading] = useState(false);

    const [dispositionModal, setDispositionModal] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await aeAPI.getDashboard();
            setData(res.data);
        } catch { } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        ipdAPI.getWards().then(r => setWards(r.data || [])).catch(() => {});
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const set = (k, v) => setVisitForm(f => ({ ...f, [k]: v }));
    const setPD = (k, v) => setPatientDetails(f => ({ ...f, [k]: v }));

    const searchPatient = async () => {
        if (!patientSearch.trim()) return;
        try {
            const res = await patientAPI.search(patientSearch);
            setSearchResults(res.data.patients);
            if (!res.data.patients.length) toast.warning('No patient found — fill in details below');
        } catch { toast.error('Search failed'); }
    };

    const selectPatient = (p) => {
        setSelectedPatient(p);
        setSearchResults([]);
        setPatientDetails({ full_name: p.full_name, phone: p.phone || '', cnic: p.cnic || '', gender: p.gender || '', age_years: p.age_years || '', name_relation: p.name_relation || 'S/O', father_name: p.father_name || '', country: p.country || 'Pakistan', city: p.city || '' });
    };

    const handleSubmit = async () => {
        if (!visitForm.triage_level) return toast.error('Please select triage level');
        if (!visitForm.chief_complaint) return toast.error('Please select chief complaint');
        if (!patientDetails.full_name.trim()) return toast.error('Please enter patient name');
        setSubmitLoading(true);
        try {
            let patient_id = selectedPatient?.id || null;
            if (!patient_id) {
                const reg = await patientAPI.register({
                    full_name: patientDetails.full_name, phone: patientDetails.phone,
                    cnic: patientDetails.cnic, gender: patientDetails.gender,
                    age_years: patientDetails.age_years || null, nationality: 'Pakistani', is_unknown: false,
                    name_relation: patientDetails.name_relation, father_name: patientDetails.father_name,
                    country: patientDetails.country, city: patientDetails.city
                });
                patient_id = reg.data.patient.id;
                toast.info(`Patient registered: ${reg.data.patient.mrn}`);
            }
            await aeAPI.createVisit({ ...visitForm, patient_id, triage_notes: visitForm.chief_complaint, vitals: {} });
            toast.success('ER visit registered!');
            setSelectedPatient(null);
            setPatientSearch('');
            setPatientDetails({ full_name: '', phone: '', cnic: '', gender: '', age_years: '', name_relation: 'S/O', father_name: '', country: 'Pakistan', city: '' });
            setVisitForm({ arrival_mode: 'Walk-in', triage_level: '', chief_complaint: '', is_mlc: false, mlc_type: '' });
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create visit');
        } finally { setSubmitLoading(false); }
    };

    const filtered = filter === 'ALL' ? data.patients : data.patients.filter(p => p.triage_color === filter);
    const overdueCount = data.patients.filter(p => getElapsed(p.arrival_time) > TRIAGE_LIMITS[p.triage_level]).length;

    return (
        <div>
            <style>{BLINK_STYLE}</style>

            {dispositionModal && (
                <DispositionModal
                    patient={{ patient_id: dispositionModal.patient_id, full_name: dispositionModal.full_name, mrn: dispositionModal.mrn, age_years: dispositionModal.age_years, gender: dispositionModal.gender, phone: dispositionModal.phone, cnic: dispositionModal.cnic }}
                    visit={dispositionModal}
                    wards={wards}
                    onClose={() => setDispositionModal(null)}
                    onDone={() => { load(); }}
                />
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 32 }}>🚨</span>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#991b1b', margin: 0 }}>ER Live Dashboard</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Auto-refreshes every 30 seconds</p>
                    </div>
                </div>
                <button onClick={load} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                    🔄 Refresh
                </button>
            </div>

            {overdueCount > 0 && (
                <div style={{ background: '#fee2e2', border: '2px solid #dc2626', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, animation: 'blink 2s ease-in-out infinite' }}>
                    <span style={{ fontSize: 20 }}>⚠️</span>
                    <span style={{ fontWeight: 700, color: '#dc2626', fontSize: 14 }}>
                        {overdueCount} patient{overdueCount > 1 ? 's' : ''} exceeded triage time limit — immediate attention required!
                    </span>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { label: 'Total Active', value: data.summary.total || 0, color: '#1e3a5f' },
                    { label: 'Immediate', value: data.summary.by_triage?.immediate || 0, color: '#ef4444' },
                    { label: 'Very Urgent', value: data.summary.by_triage?.very_urgent || 0, color: '#f97316' },
                    { label: 'Urgent', value: data.summary.by_triage?.urgent || 0, color: '#eab308' },
                    { label: 'Standard', value: data.summary.by_triage?.standard || 0, color: '#22c55e' },
                    { label: 'MLC Cases', value: data.summary.mlc_cases || 0, color: '#7c3aed' },
                ].map(c => (
                    <div key={c.label} style={{ background: 'white', borderRadius: 8, padding: '14px 16px', borderLeft: `4px solid ${c.color}`, border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{c.label}</div>
                    </div>
                ))}
            </div>

            {/* New ER Visit Form */}
            <div style={{ background: '#fff5f5', borderRadius: 12, border: '2px solid #fecaca', padding: 20, marginBottom: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#991b1b', margin: '0 0 16px 0' }}>Register New ER Visit</h3>
                <div style={{ background: 'white', borderRadius: 10, padding: 18, marginBottom: 14, border: '1px solid #e2e8f0' }}>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Patient Details</h4>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                        <input value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                            placeholder="Search existing patient — MRN / CNIC / Phone / Name"
                            style={{ ...inputStyle, flex: 1 }}
                            onKeyDown={e => e.key === 'Enter' && searchPatient()} />
                        <button onClick={searchPatient} style={{ padding: '9px 18px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>Search</button>
                    </div>
                    {searchResults.map(p => (
                        <div key={p.id} onClick={() => selectPatient(p)}
                            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', marginBottom: 4, fontSize: 13 }}>
                            <strong>{p.mrn}</strong> — {p.full_name} | {p.gender} | {p.phone}
                        </div>
                    ))}
                    {selectedPatient && (
                        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, padding: 10, fontSize: 13, fontWeight: 600, color: '#166534', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <span>✅ {selectedPatient.full_name} ({selectedPatient.mrn})</span>
                            <button onClick={() => { setSelectedPatient(null); setPatientDetails({ full_name: '', phone: '', cnic: '', gender: '', age_years: '' }); }}
                                style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Remove</button>
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Full Name *</label>
                            <input style={{ ...inputStyle, background: selectedPatient ? '#f9fafb' : 'white' }} value={patientDetails.full_name}
                                onChange={e => setPD('full_name', e.target.value)} placeholder="Patient full name" disabled={!!selectedPatient} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Relation / Guardian Name</label>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <select style={{ ...inputStyle, width: 90, flexShrink: 0, background: selectedPatient ? '#f9fafb' : 'white' }}
                                    value={patientDetails.name_relation} onChange={e => setPD('name_relation', e.target.value)} disabled={!!selectedPatient}>
                                    {RELATIONS.map(r => <option key={r}>{r}</option>)}
                                </select>
                                <input style={{ ...inputStyle, flex: 1, background: selectedPatient ? '#f9fafb' : 'white' }} value={patientDetails.father_name}
                                    onChange={e => setPD('father_name', e.target.value)} placeholder="Guardian name" disabled={!!selectedPatient} />
                            </div>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Phone</label>
                            <input style={{ ...inputStyle, background: selectedPatient ? '#f9fafb' : 'white' }} value={patientDetails.phone}
                                onChange={e => setPD('phone', e.target.value)} placeholder="0300-1234567" disabled={!!selectedPatient} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>CNIC / B-Form</label>
                            <input style={{ ...inputStyle, background: selectedPatient ? '#f9fafb' : 'white' }} value={patientDetails.cnic}
                                onChange={e => setPD('cnic', e.target.value)} placeholder="42301-1234567-8" disabled={!!selectedPatient} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Gender</label>
                            <select style={{ ...inputStyle, background: selectedPatient ? '#f9fafb' : 'white' }} value={patientDetails.gender}
                                onChange={e => setPD('gender', e.target.value)} disabled={!!selectedPatient}>
                                <option value="">Select</option>
                                <option>Male</option><option>Female</option><option>Other</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Age (yrs)</label>
                            <input style={{ ...inputStyle, background: selectedPatient ? '#f9fafb' : 'white' }} value={patientDetails.age_years}
                                onChange={e => setPD('age_years', e.target.value)} placeholder="35" type="number" disabled={!!selectedPatient} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Country</label>
                            <select style={{ ...inputStyle, background: selectedPatient ? '#f9fafb' : 'white' }}
                                value={patientDetails.country} onChange={e => { setPD('country', e.target.value); setPD('city', ''); }} disabled={!!selectedPatient}>
                                {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>City</label>
                            <select style={{ ...inputStyle, background: selectedPatient ? '#f9fafb' : 'white' }}
                                value={patientDetails.city} onChange={e => setPD('city', e.target.value)} disabled={!!selectedPatient}>
                                <option value="">— Select City —</option>
                                {(CITIES[patientDetails.country] || []).map(c => <option key={c}>{c}</option>)}
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div style={{ background: 'white', borderRadius: 10, padding: 18, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 14 }}>Visit Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Arrival Mode</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={visitForm.arrival_mode} onChange={e => set('arrival_mode', e.target.value)}>
                                <option>Walk-in</option><option>Ambulance</option><option>Police Case</option><option>Transfer</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Chief Complaint *</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={visitForm.chief_complaint} onChange={e => set('chief_complaint', e.target.value)}>
                                <option value="">— Select Complaint —</option>
                                {CHIEF_COMPLAINTS.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Triage Level *</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={visitForm.triage_level} onChange={e => set('triage_level', parseInt(e.target.value))}>
                                <option value="">— Select Triage —</option>
                                {TRIAGE_LEVELS.map(t => <option key={t.level} value={t.level}>{t.label} — {t.example}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>MLC Type (if applicable)</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={visitForm.mlc_type} onChange={e => { set('mlc_type', e.target.value); set('is_mlc', !!e.target.value); }}>
                                <option value="">— Not MLC —</option>
                                {['RTA', 'Assault', 'Poisoning', 'Burns', 'Drowning', 'Fall from Height', 'Industrial Accident', 'Other'].map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                <button onClick={handleSubmit} disabled={submitLoading}
                    style={{ padding: '12px 32px', background: submitLoading ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                    {submitLoading ? 'Registering...' : '✅ Confirm Patient & Register ER Visit'}
                </button>
            </div>

            {/* Filter buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['ALL', 'RED', 'ORANGE', 'YELLOW', 'GREEN', 'BLUE'].map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                        style={{ padding: '6px 16px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, fontWeight: filter === f ? 700 : 400,
                            background: filter === f ? (TRIAGE_COLORS[f] || '#1e3a5f') : 'white',
                            color: filter === f ? (f === 'YELLOW' ? '#1e293b' : 'white') : '#374151' }}>
                        {f}
                    </button>
                ))}
            </div>

            {/* Patient board */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading patients...</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                    <p style={{ color: '#64748b' }}>No active patients in ER</p>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Triage', 'AE No.', 'Patient', 'Age/Sex', 'Arrival', 'Timer', 'Bed', 'MLC', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '12px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p) => {
                                    const elapsed = getElapsed(p.arrival_time);
                                    const limit = TRIAGE_LIMITS[p.triage_level];
                                    const overdue = elapsed > limit;
                                    return (
                                        <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9', background: overdue ? '#fff5f5' : 'white' }}>
                                            <td style={{ padding: '10px 10px' }}>
                                                <div style={{ background: TRIAGE_COLORS[p.triage_color] || '#9ca3af', color: p.triage_color === 'YELLOW' ? '#1e293b' : 'white', padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    T{p.triage_level} {p.triage_color}
                                                </div>
                                            </td>
                                            <td style={{ padding: '10px 10px', fontSize: 12, fontWeight: 700, color: '#1e3a5f' }}>{p.ae_number}</td>
                                            <td style={{ padding: '10px 10px' }}>
                                                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.full_name || 'Unknown'}</div>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>{p.mrn}</div>
                                            </td>
                                            <td style={{ padding: '10px 10px', fontSize: 12 }}>{p.age_years ? `${p.age_years}y` : '—'} / {p.gender || '—'}</td>
                                            <td style={{ padding: '10px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(p.arrival_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}</td>
                                            <td style={{ padding: '10px 10px' }}>
                                                <TriageTimer arrivalTime={p.arrival_time} triageLevel={p.triage_level} />
                                            </td>
                                            <td style={{ padding: '10px 10px', fontSize: 12 }}>
                                                {p.bed_zone ? `${p.bed_zone} ${p.bed_number || ''}` : <span style={{ color: '#f97316', fontSize: 11 }}>Unassigned</span>}
                                            </td>
                                            <td style={{ padding: '10px 10px' }}>
                                                {p.is_mlc && <span style={{ background: '#7c3aed', color: 'white', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, display: 'block', textAlign: 'center', marginBottom: 3 }}>MLC</span>}
                                                {p.mci_tag && (
                                                    <span style={{ background: MCI_TAG_COLORS[p.mci_tag]?.bg || '#374151', color: MCI_TAG_COLORS[p.mci_tag]?.text || 'white', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, display: 'block', textAlign: 'center' }}>
                                                        MCI {MCI_TAG_COLORS[p.mci_tag]?.label || p.mci_tag}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px 10px' }}>
                                                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                                    <button onClick={() => navigate(`/ae/clinical/${p.id}`)}
                                                        style={{ padding: '4px 8px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                        MAR/IV
                                                    </button>
                                                    <button onClick={() => setDispositionModal(p)}
                                                        style={{ padding: '4px 8px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                        Disposition
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
